import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getDripCampaigns = createAsyncThunk(
    'dripCampaigns/getDripCampaigns',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.DRIP_CAMPAIGNS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaigns retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch campaigns',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get drip campaigns error:', error);
            let errorMessage = 'Failed to fetch campaigns';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch campaigns';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getDripCampaign = createAsyncThunk(
    'dripCampaigns/getDripCampaign',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.DRIP_CAMPAIGN(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign retrieved successfully',
                    campaign: data?.campaign || null,
                    analytics: data?.analytics || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch campaign',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get drip campaign error:', error);
            let errorMessage = 'Failed to fetch campaign';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch campaign';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createDripCampaign = createAsyncThunk(
    'dripCampaigns/createDripCampaign',
    async (campaignData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Creating drip campaign with data:', campaignData);

            const response = await apiClient.post(API_ENDPOINTS.CREATE_DRIP_CAMPAIGN, campaignData);
            console.log('✅ Create campaign response:', response.data);

            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                const campaign = data ? {
                    ...data,
                    steps: Array.isArray(data.steps) ? data.steps : []
                } : null;

                return {
                    success: true,
                    message: message || 'Campaign created successfully',
                    campaign: campaign
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create campaign',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Create drip campaign error:', error);
            let errorMessage = 'Failed to create campaign';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to create campaign';
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateDripCampaign = createAsyncThunk(
    'dripCampaigns/updateDripCampaign',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Updating drip campaign:', id, data);

            const response = await apiClient.put(API_ENDPOINTS.UPDATE_DRIP_CAMPAIGN(id), data);
            console.log('✅ Update campaign response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                const campaign = responseData ? {
                    ...responseData,
                    steps: Array.isArray(responseData.steps) ? responseData.steps : []
                } : null;

                return {
                    success: true,
                    message: message || 'Campaign updated successfully',
                    campaign: campaign
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update campaign',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Update drip campaign error:', error);
            let errorMessage = 'Failed to update campaign';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to update campaign';
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deleteDripCampaign = createAsyncThunk(
    'dripCampaigns/deleteDripCampaign',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Deleting drip campaign:', id);

            const response = await apiClient.delete(API_ENDPOINTS.DELETE_DRIP_CAMPAIGN(id));
            console.log('✅ Delete campaign response:', response.data);

            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete campaign',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Delete drip campaign error:', error);
            let errorMessage = 'Failed to delete campaign';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to delete campaign';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const toggleDripCampaignStatus = createAsyncThunk(
    'dripCampaigns/toggleStatus',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Toggling drip campaign status:', id);

            const response = await apiClient.post(API_ENDPOINTS.DRIP_CAMPAIGN_TOGGLE(id));
            console.log('✅ Toggle campaign response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign status toggled',
                    id: id,
                    is_active: data?.is_active || false
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to toggle campaign status',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Toggle drip campaign status error:', error);
            let errorMessage = 'Failed to toggle campaign status';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to toggle campaign status';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const duplicateDripCampaign = createAsyncThunk(
    'dripCampaigns/duplicate',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Duplicating drip campaign:', id);

            const response = await apiClient.post(API_ENDPOINTS.DRIP_CAMPAIGN_DUPLICATE(id));
            console.log('✅ Duplicate campaign response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                const campaign = data ? {
                    ...data,
                    steps: Array.isArray(data.steps) ? data.steps : []
                } : null;

                return {
                    success: true,
                    message: message || 'Campaign duplicated successfully',
                    campaign: campaign
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to duplicate campaign',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Duplicate drip campaign error:', error);
            let errorMessage = 'Failed to duplicate campaign';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to duplicate campaign';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getDripCampaignStats = createAsyncThunk(
    'dripCampaigns/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Fetching drip campaign stats:', params);

            const response = await apiClient.get(API_ENDPOINTS.DRIP_CAMPAIGN_STATS, { params });
            console.log('✅ Campaign stats response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign statistics retrieved',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch campaign statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Get drip campaign stats error:', error);
            let errorMessage = 'Failed to fetch campaign statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch campaign statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getDripCampaignAnalytics = createAsyncThunk(
    'dripCampaigns/getAnalytics',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Fetching drip campaign analytics:', id);

            const response = await apiClient.get(API_ENDPOINTS.DRIP_CAMPAIGN_ANALYTICS(id));
            console.log('✅ Campaign analytics response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign analytics retrieved',
                    analytics: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch campaign analytics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Get drip campaign analytics error:', error);
            let errorMessage = 'Failed to fetch campaign analytics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch campaign analytics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// src/store/slices/growth/dripCampaignSlice.js

/**
 * Send broadcast to all enrolled members in a campaign
 */
export const sendBroadcastToCampaign = createAsyncThunk(
    'dripCampaigns/sendBroadcast',
    async ({ id, message, sender_id = null }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Sending broadcast to campaign:', id);

            const response = await apiClient.post(API_ENDPOINTS.DRIP_CAMPAIGN_BROADCAST(id), {
                message,
                sender_id
            });
            console.log('✅ Broadcast response:', response.data);

            const { status, message: responseMessage, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: responseMessage || 'Broadcast sent successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: responseMessage || 'Failed to send broadcast',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Send broadcast error:', error);
            let errorMessage = 'Failed to send broadcast';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to send broadcast';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Trigger the campaign flow for all enrolled members
 */
export const triggerCampaignFlow = createAsyncThunk(
    'dripCampaigns/triggerFlow',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Triggering campaign flow:', id);

            const response = await apiClient.post(API_ENDPOINTS.DRIP_CAMPAIGN_TRIGGER_FLOW(id));
            console.log('✅ Trigger flow response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign flow triggered successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to trigger campaign flow',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Trigger flow error:', error);
            let errorMessage = 'Failed to trigger campaign flow';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to trigger campaign flow';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// =============================================
// GET DRIP CAMPAIGN ENROLLMENTS - FIXED
// =============================================

export const getDripCampaignEnrollments = createAsyncThunk(
    'dripCampaigns/getEnrollments',
    async ({ id, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Fetching drip campaign enrollments:', id, params);

            const response = await apiClient.get(API_ENDPOINTS.DRIP_CAMPAIGN_ENROLLMENTS(id), { params });
            console.log('✅ Campaign enrollments response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                // The response structure is: { data: { data: [...], meta: {...} } }
                // We need to extract data.data for enrollments and data.meta for pagination
                return {
                    success: true,
                    message: message || 'Campaign enrollments retrieved successfully',
                    enrollments: data?.data || [],
                    pagination: data?.meta || {
                        current_page: 1,
                        last_page: 1,
                        per_page: 15,
                        total: 0
                    }
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch campaign enrollments',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Get drip campaign enrollments error:', error);
            let errorMessage = 'Failed to fetch campaign enrollments';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch campaign enrollments';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const enrollMemberInCampaign = createAsyncThunk(
    'dripCampaigns/enrollMember',
    async ({ id, member_id }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Enrolling member in campaign:', id, member_id);

            const response = await apiClient.post(API_ENDPOINTS.DRIP_CAMPAIGN_ENROLL(id), { member_id });
            console.log('✅ Enroll member response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Member enrolled successfully',
                    enrollment: data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to enroll member',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Enroll member in campaign error:', error);
            let errorMessage = 'Failed to enroll member';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to enroll member';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const unenrollMemberFromCampaign = createAsyncThunk(
    'dripCampaigns/unenrollMember',
    async ({ id, member_id }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Unenrolling member from campaign:', id, member_id);

            const response = await apiClient.post(API_ENDPOINTS.DRIP_CAMPAIGN_UNENROLL(id), { member_id });
            console.log('✅ Unenroll member response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Member unenrolled successfully',
                    campaign: data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to unenroll member',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Unenroll member from campaign error:', error);
            let errorMessage = 'Failed to unenroll member';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to unenroll member';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const exportDripCampaign = createAsyncThunk(
    'dripCampaigns/export',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Exporting drip campaign:', id);

            const response = await apiClient.get(API_ENDPOINTS.DRIP_CAMPAIGN_EXPORT(id));
            console.log('✅ Export campaign response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign exported successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to export campaign',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Export drip campaign error:', error);
            let errorMessage = 'Failed to export campaign';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to export campaign';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    campaigns: [],
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    currentCampaign: null,
    analytics: null,
    enrollments: [],
    enrollmentsPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    stats: null,
    isLoading: false,
    isSubmitting: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const dripCampaignSlice = createSlice({
    name: 'dripCampaigns',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearDripCampaignState: (state) => {
            state.campaigns = [];
            state.pagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.currentCampaign = null;
            state.analytics = null;
            state.enrollments = [];
            state.enrollmentsPagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.stats = null;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
            state.isSubmitting = false;
        },
        clearCurrentCampaign: (state) => {
            state.currentCampaign = null;
            state.analytics = null;
            state.enrollments = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET DRIP CAMPAIGNS =====
            .addCase(getDripCampaigns.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getDripCampaigns.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};

                const campaigns = (responseData.data || []).map(campaign => ({
                    ...campaign,
                    steps: Array.isArray(campaign.steps) ? campaign.steps : []
                }));

                state.campaigns = campaigns;
                state.pagination = responseData.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = payload.message || 'Campaigns retrieved successfully';
                state.apiMessage = payload.message || 'Campaigns retrieved successfully';
                state.error = null;
            })
            .addCase(getDripCampaigns.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch campaigns';
                state.apiMessage = action.payload?.message || 'Failed to fetch campaigns';
                state.successMessage = null;
                state.campaigns = [];
                state.pagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
            })

            // ===== GET DRIP CAMPAIGN =====
            .addCase(getDripCampaign.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getDripCampaign.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};

                const campaign = payload.campaign ? {
                    ...payload.campaign,
                    steps: Array.isArray(payload.campaign.steps) ? payload.campaign.steps : []
                } : null;

                state.currentCampaign = campaign;
                state.analytics = payload.analytics || null;
                state.successMessage = payload.message || 'Campaign retrieved successfully';
                state.apiMessage = payload.message || 'Campaign retrieved successfully';
                state.error = null;
            })
            .addCase(getDripCampaign.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch campaign';
                state.apiMessage = action.payload?.message || 'Failed to fetch campaign';
                state.successMessage = null;
                state.currentCampaign = null;
                state.analytics = null;
            })

            // ===== CREATE DRIP CAMPAIGN =====
            .addCase(createDripCampaign.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createDripCampaign.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Campaign created successfully';
                state.apiMessage = payload.message || 'Campaign created successfully';
                state.error = null;

                if (payload.campaign) {
                    const campaign = {
                        ...payload.campaign,
                        steps: Array.isArray(payload.campaign.steps) ? payload.campaign.steps : []
                    };
                    state.campaigns = [campaign, ...state.campaigns];
                }
            })
            .addCase(createDripCampaign.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create campaign';
                state.apiMessage = action.payload?.message || 'Failed to create campaign';
                state.successMessage = null;
            })

            // ===== UPDATE DRIP CAMPAIGN =====
            .addCase(updateDripCampaign.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateDripCampaign.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Campaign updated successfully';
                state.apiMessage = payload.message || 'Campaign updated successfully';
                state.error = null;

                if (payload.campaign) {
                    const campaign = {
                        ...payload.campaign,
                        steps: Array.isArray(payload.campaign.steps) ? payload.campaign.steps : []
                    };
                    state.campaigns = state.campaigns.map(c =>
                        c.id === campaign.id ? campaign : c
                    );
                    if (state.currentCampaign?.id === campaign.id) {
                        state.currentCampaign = campaign;
                    }
                }
            })
            .addCase(updateDripCampaign.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update campaign';
                state.apiMessage = action.payload?.message || 'Failed to update campaign';
                state.successMessage = null;
            })

            // ===== DELETE DRIP CAMPAIGN =====
            .addCase(deleteDripCampaign.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteDripCampaign.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Campaign deleted successfully';
                state.apiMessage = payload.message || 'Campaign deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.campaigns = state.campaigns.filter(c => c.id !== payload.id);
                    if (state.currentCampaign?.id === payload.id) {
                        state.currentCampaign = null;
                        state.analytics = null;
                    }
                }
            })
            .addCase(deleteDripCampaign.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete campaign';
                state.apiMessage = action.payload?.message || 'Failed to delete campaign';
                state.successMessage = null;
            })

            // ===== TOGGLE CAMPAIGN STATUS =====
            .addCase(toggleDripCampaignStatus.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(toggleDripCampaignStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Campaign status toggled';
                state.apiMessage = payload.message || 'Campaign status toggled';
                state.error = null;
                if (payload.id && payload.is_active !== undefined) {
                    state.campaigns = state.campaigns.map(c =>
                        c.id === payload.id ? { ...c, is_active: payload.is_active } : c
                    );
                    if (state.currentCampaign?.id === payload.id) {
                        state.currentCampaign = { ...state.currentCampaign, is_active: payload.is_active };
                    }
                }
            })
            .addCase(toggleDripCampaignStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to toggle campaign status';
                state.apiMessage = action.payload?.message || 'Failed to toggle campaign status';
                state.successMessage = null;
            })

            // ===== DUPLICATE CAMPAIGN =====
            .addCase(duplicateDripCampaign.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(duplicateDripCampaign.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Campaign duplicated successfully';
                state.apiMessage = payload.message || 'Campaign duplicated successfully';
                state.error = null;
                if (payload.campaign) {
                    const campaign = {
                        ...payload.campaign,
                        steps: Array.isArray(payload.campaign.steps) ? payload.campaign.steps : []
                    };
                    state.campaigns = [campaign, ...state.campaigns];
                }
            })
            .addCase(duplicateDripCampaign.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to duplicate campaign';
                state.apiMessage = action.payload?.message || 'Failed to duplicate campaign';
                state.successMessage = null;
            })

            // ===== GET CAMPAIGN STATS =====
            .addCase(getDripCampaignStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getDripCampaignStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.stats = payload.stats || null;
                state.successMessage = payload.message || 'Campaign statistics retrieved';
                state.apiMessage = payload.message || 'Campaign statistics retrieved';
                state.error = null;
            })
            .addCase(getDripCampaignStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch campaign statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch campaign statistics';
                state.successMessage = null;
                state.stats = null;
            })

            // ===== GET CAMPAIGN ANALYTICS =====
            .addCase(getDripCampaignAnalytics.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getDripCampaignAnalytics.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.analytics = payload.analytics || null;
                state.successMessage = payload.message || 'Campaign analytics retrieved';
                state.apiMessage = payload.message || 'Campaign analytics retrieved';
                state.error = null;
            })
            .addCase(getDripCampaignAnalytics.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch campaign analytics';
                state.apiMessage = action.payload?.message || 'Failed to fetch campaign analytics';
                state.successMessage = null;
                state.analytics = null;
            })

            // ===== GET CAMPAIGN ENROLLMENTS (FIXED) =====
            .addCase(getDripCampaignEnrollments.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getDripCampaignEnrollments.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                // FIX: Use payload.enrollments directly
                state.enrollments = payload.enrollments || [];
                state.enrollmentsPagination = payload.pagination || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = payload.message || 'Campaign enrollments retrieved successfully';
                state.apiMessage = payload.message || 'Campaign enrollments retrieved successfully';
                state.error = null;

                // Log for debugging
                console.log('📊 Enrollments set in Redux:', state.enrollments);
            })
            .addCase(getDripCampaignEnrollments.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch campaign enrollments';
                state.apiMessage = action.payload?.message || 'Failed to fetch campaign enrollments';
                state.successMessage = null;
                state.enrollments = [];
                state.enrollmentsPagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
            })

            // ===== ENROLL MEMBER =====
            .addCase(enrollMemberInCampaign.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(enrollMemberInCampaign.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Member enrolled successfully';
                state.apiMessage = payload.message || 'Member enrolled successfully';
                state.error = null;
            })
            .addCase(enrollMemberInCampaign.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to enroll member';
                state.apiMessage = action.payload?.message || 'Failed to enroll member';
                state.successMessage = null;
            })

            // ===== UNENROLL MEMBER =====
            .addCase(unenrollMemberFromCampaign.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(unenrollMemberFromCampaign.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Member unenrolled successfully';
                state.apiMessage = payload.message || 'Member unenrolled successfully';
                state.error = null;
            })
            .addCase(unenrollMemberFromCampaign.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to unenroll member';
                state.apiMessage = action.payload?.message || 'Failed to unenroll member';
                state.successMessage = null;
            })

            // ===== EXPORT CAMPAIGN =====
            .addCase(exportDripCampaign.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportDripCampaign.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Campaign exported successfully';
                state.apiMessage = payload.message || 'Campaign exported successfully';
                state.error = null;
            })
            .addCase(exportDripCampaign.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export campaign';
                state.apiMessage = action.payload?.message || 'Failed to export campaign';
                state.successMessage = null;
            })

            // In the extraReducers section of dripCampaignSlice.js

            // ===== SEND BROADCAST =====
            .addCase(sendBroadcastToCampaign.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(sendBroadcastToCampaign.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Broadcast sent successfully';
                state.apiMessage = payload.message || 'Broadcast sent successfully';
                state.error = null;
            })
            .addCase(sendBroadcastToCampaign.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send broadcast';
                state.apiMessage = action.payload?.message || 'Failed to send broadcast';
                state.successMessage = null;
            })

            // ===== TRIGGER CAMPAIGN FLOW =====
            .addCase(triggerCampaignFlow.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(triggerCampaignFlow.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Campaign flow triggered successfully';
                state.apiMessage = payload.message || 'Campaign flow triggered successfully';
                state.error = null;
            })
            .addCase(triggerCampaignFlow.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to trigger campaign flow';
                state.apiMessage = action.payload?.message || 'Failed to trigger campaign flow';
                state.successMessage = null;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearDripCampaignState,
    clearCurrentCampaign,
} = dripCampaignSlice.actions;

export const selectDripCampaigns = (state) => state.dripCampaigns?.campaigns || [];
export const selectDripCampaignPagination = (state) => state.dripCampaigns?.pagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectCurrentDripCampaign = (state) => state.dripCampaigns?.currentCampaign || null;
export const selectDripCampaignAnalytics = (state) => state.dripCampaigns?.analytics || null;
export const selectDripCampaignEnrollments = (state) => state.dripCampaigns?.enrollments || [];
export const selectDripCampaignEnrollmentsPagination = (state) => state.dripCampaigns?.enrollmentsPagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectDripCampaignStats = (state) => state.dripCampaigns?.stats || null;
export const selectDripCampaignsLoading = (state) => state.dripCampaigns?.isLoading || false;
export const selectDripCampaignsSubmitting = (state) => state.dripCampaigns?.isSubmitting || false;
export const selectDripCampaignsError = (state) => state.dripCampaigns?.error || null;
export const selectDripCampaignsApiMessage = (state) => state.dripCampaigns?.apiMessage || null;

export default dripCampaignSlice.reducer;